import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {IntersectionObserver | undefined} */
  #prefetchObserver;

  /** @type {boolean} */
  #isPrefetching = false;

  // Not a DOM attribute: the filter re-render on load strips attributes the server didn't send, which wired
  // the button twice so one click loaded four pages at once and the extra images left cards blank
  /** @type {WeakSet<HTMLButtonElement>} */
  #wiredLoadMoreButtons = new WeakSet();

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // Observe view more first for immediate functionality
    this.#observeViewMore();
    
    // Setup early prefetching triggers
    this.#setupEarlyPrefetch();
    
    // Defer initial prefetching to allow first page to load faster
    const viewMoreNext = this.refs.viewMoreNext;
    const isButtonMode = !!viewMoreNext?.querySelector('button[ref="loadMoreButton"]');
    
    requestIdleCallback(() => {
      const isMobile = window.innerWidth < 750;
      // Reduce prefetch count on mobile for better performance
      const prefetchCount = isMobile ? (isButtonMode ? 2 : 1) : (isButtonMode ? 3 : 1);
      this.#prefetchMultiplePages('next', prefetchCount);
      this.#fetchPage('previous');
    }, { timeout: 2000 });

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    if (this.#prefetchObserver) {
      this.#prefetchObserver.disconnect();
    }
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #setupEarlyPrefetch() {
    const { viewMoreNext } = this.refs;
    if (!viewMoreNext) return;

    // Setup intersection observer with large margin to prefetch before button is visible
    this.#prefetchObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#triggerEarlyPrefetch();
          }
        }
      },
      { rootMargin: '1500px' } // Start prefetching when within 1500px of the button
    );
    
    this.#prefetchObserver.observe(viewMoreNext);
  }

  #triggerEarlyPrefetch() {
    if (this.#isPrefetching) return;
    this.#isPrefetching = true;
    
    const { viewMoreNext } = this.refs;
    const isButtonMode = !!viewMoreNext?.querySelector('button[ref="loadMoreButton"]');
    const isMobile = window.innerWidth < 750;
    
    // Reduce prefetch count on mobile for better performance
    const prefetchCount = isMobile ? (isButtonMode ? 2 : 1) : (isButtonMode ? 3 : 2);
    this.#prefetchMultiplePages('next', prefetchCount);
    
    // Reset prefetch flag after a delay to allow re-prefetching if user scrolls away and back
    setTimeout(() => {
      this.#isPrefetching = false;
    }, 5000);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Check if there's a button inside viewMoreNext (button mode)
    const loadMoreButton = viewMoreNext?.querySelector('button[ref="loadMoreButton"]');
    
    if (loadMoreButton) {
      if (!this.#wiredLoadMoreButtons.has(loadMoreButton)) {
        this.#wiredLoadMoreButtons.add(loadMoreButton);
        loadMoreButton.addEventListener('click', async () => {
          if (viewTransition.current) await viewTransition.current;
          await this.#renderNextPage();
          this.#updateLoadMoreButton();
        });
        // Prefetch on hover - gives ~100-500ms head start before click
        loadMoreButton.addEventListener('mouseenter', () => this.#triggerEarlyPrefetch(), { passive: true });
        loadMoreButton.addEventListener('focus', () => this.#triggerEarlyPrefetch(), { passive: true });
        loadMoreButton.addEventListener('touchstart', () => this.#triggerEarlyPrefetch(), { passive: true });
      }
      this.#updateLoadMoreButton();
      return;
    }

    // Auto-scroll mode: use intersection observer
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '600px',
        }
      );
    }

    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  #updateLoadMoreButton() {
    const { viewMoreNext, grid } = this.refs;
    if (!viewMoreNext || !grid) return;

    const loadMoreButton = viewMoreNext.querySelector('button[ref="loadMoreButton"]');
    if (!loadMoreButton) return;

    const nextPage = this.#getPage('next');
    const hasMorePages = nextPage && this.#shouldUsePage(nextPage);

    if (!hasMorePages) {
      viewMoreNext.style.display = 'none';
    } else {
      viewMoreNext.style.display = '';
      const isLoading = loadMoreButton.disabled;
      if (!isLoading) {
        loadMoreButton.disabled = false;
      }
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * Pre-fetch multiple pages ahead for smoother scrolling
   * @param {"previous" | "next"} direction
   * @param {number} count - Number of pages to pre-fetch
   */
  async #prefetchMultiplePages(direction, count) {
    const { grid } = this.refs;
    const lastPage = Number(grid?.dataset.lastPage || 1);
    const currentPage = this.#getCurrentPage();

    const pagesToFetch = [];
    for (let i = 1; i <= count; i++) {
      const pageNum = direction === 'next' ? currentPage + i : currentPage - i;
      if (pageNum >= 1 && pageNum <= lastPage && !this.pages.has(pageNum)) {
        pagesToFetch.push(pageNum);
      }
    }

    // Fetch pages in parallel for faster prefetching
    if (pagesToFetch.length > 0) {
      await Promise.all(pagesToFetch.map(page => this.#fetchSpecificPage(page)));
    }
  }

  /**
   * Gets the current page number from the first or last card
   * @returns {number}
   */
  #getCurrentPage() {
    const { cards } = this.refs;
    if (!Array.isArray(cards) || cards.length === 0) return 1;
    return Number(cards[0].dataset.page) || 1;
  }

  async #renderNextPage() {
    const { grid, viewMoreNext } = this.refs;

    if (!grid) return;

    const currentPage = this.#getCurrentPage();
    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    const loadMoreButton = viewMoreNext?.querySelector('button[ref="loadMoreButton"]');
    const isButtonMode = !!loadMoreButton;
    const isMobile = window.innerWidth < 750;
    
    // Reduce batch size on mobile for smoother loading
    // Button mode: load 1 page on mobile (24 products), 2 pages on desktop (48 products)
    // Auto-scroll: always load 1 page (32 products)
    const pagesToLoad = isButtonMode ? (isMobile ? 1 : 2) : 1;
    
    if (loadMoreButton) {
      loadMoreButton.disabled = true;
      const loadingSpinner = viewMoreNext.querySelector('.loading-spinner');
      if (loadingSpinner) {
        loadingSpinner.style.display = 'block';
      }
    }

    const lastPage = Number(grid?.dataset.lastPage || 1);
    const fragment = document.createDocumentFragment();
    let lastLoadedPage = nextPage.page;

    // Fetch all pages in parallel for faster loading
    const pagesToFetch = [];
    for (let i = 0; i < pagesToLoad; i++) {
      const pageNum = nextPage.page + i;
      if (pageNum > lastPage) break;
      
      let pageItemElements = this.#getGridForPage(pageNum);
      if (!pageItemElements) {
        pagesToFetch.push(pageNum);
      } else {
        // Already cached, add to fragment immediately
        fragment.append(...pageItemElements);
        lastLoadedPage = pageNum;
      }
    }

    // Fetch missing pages in parallel
    if (pagesToFetch.length > 0) {
      try {
        await Promise.all(pagesToFetch.map(pageNum => this.#fetchSpecificPage(pageNum)));
        
        // Now add all fetched pages to fragment
        for (const pageNum of pagesToFetch) {
          const pageItemElements = this.#getGridForPage(pageNum);
          if (pageItemElements && pageItemElements.length > 0) {
            fragment.append(...pageItemElements);
            lastLoadedPage = pageNum;
          }
        }
      } catch (error) {
        console.error('Failed to load pages:', error);
      }
    }

    if (fragment.children.length === 0) {
      if (loadMoreButton) {
        loadMoreButton.disabled = false;
        const loadingSpinner = viewMoreNext.querySelector('.loading-spinner');
        if (loadingSpinner) {
          loadingSpinner.style.display = 'none';
        }
      }
      return;
    }

    // Use progressive rendering for smoother UX, especially on mobile
    const itemsToRender = Array.from(fragment.children);
    const batchSize = isMobile ? 8 : 16;

    // Progressive rendering: add items in batches using requestAnimationFrame
    if (isMobile && itemsToRender.length > batchSize) {
      // Mark grid as rendering for CSS animations
      grid.setAttribute('data-rendering', '');
      
      // On mobile, render progressively for smoother experience
      let currentIndex = 0;
      
      const renderBatch = () => {
        const endIndex = Math.min(currentIndex + batchSize, itemsToRender.length);
        const batch = itemsToRender.slice(currentIndex, endIndex);
        
        if (batch.length > 0) {
          // Mark items as newly loaded for animation
          batch.forEach(item => {
            if (item instanceof HTMLElement) {
              item.setAttribute('data-newly-loaded', '');
              // Remove attribute after animation completes
              setTimeout(() => {
                item.removeAttribute('data-newly-loaded');
              }, 300);
            }
          });
          
          // Append batch to DOM
          grid.append(...batch);
          
          // Process aspect ratios for this batch after DOM append
          if (this.#aspectRatioHelper) {
            requestIdleCallback(() => {
              this.#aspectRatioHelper.processNewElements();
            }, { timeout: 100 });
          }
          
          currentIndex = endIndex;
          
          if (currentIndex < itemsToRender.length) {
            // Yield to browser for smoother rendering
            requestAnimationFrame(renderBatch);
          } else {
            // All items rendered, remove rendering state
            requestAnimationFrame(() => {
              grid.removeAttribute('data-rendering');
            });
            // Finish up
            this.#finishPageRender(loadMoreButton, viewMoreNext, lastLoadedPage, lastPage, isButtonMode, isMobile);
          }
        }
      };
      
      // Start rendering first batch immediately
      requestAnimationFrame(renderBatch);
    } else {
      // Desktop or small batch: render all at once
      // Mark items as newly loaded for animation
      Array.from(fragment.children).forEach(item => {
        if (item instanceof HTMLElement) {
          item.setAttribute('data-newly-loaded', '');
          // Remove attribute after animation completes
          setTimeout(() => {
            item.removeAttribute('data-newly-loaded');
          }, 300);
        }
      });
      
      grid.append(fragment);
      
      // Process aspect ratios after DOM append
      if (this.#aspectRatioHelper) {
        requestIdleCallback(() => {
          this.#aspectRatioHelper.processNewElements();
        }, { timeout: 100 });
      }
      
      this.#finishPageRender(loadMoreButton, viewMoreNext, lastLoadedPage, lastPage, isButtonMode, isMobile);
    }
  }

  /**
   * Finishes the page render by updating UI state and prefetching next pages
   * @param {HTMLButtonElement | null} loadMoreButton
   * @param {HTMLElement | null} viewMoreNext
   * @param {number} lastLoadedPage
   * @param {number} lastPage
   * @param {boolean} isButtonMode
   * @param {boolean} isMobile
   */
  #finishPageRender(loadMoreButton, viewMoreNext, lastLoadedPage, lastPage, isButtonMode, isMobile) {
    // Update URL asynchronously
    requestIdleCallback(() => {
      const url = new URL(window.location.href);
      url.searchParams.set('page', Math.min(lastLoadedPage, lastPage).toString());
      url.hash = '';
      history.pushState('', '', url.toString());
    });

    if (loadMoreButton) {
      loadMoreButton.disabled = false;
      const loadingSpinner = viewMoreNext?.querySelector('.loading-spinner');
      if (loadingSpinner) {
        loadingSpinner.style.display = 'none';
      }
    }

    this.#updateLoadMoreButton();

    // Prefetch next pages immediately for faster subsequent loads
    this.#isPrefetching = false;
    const prefetchCount = isMobile ? (isButtonMode ? 2 : 1) : (isButtonMode ? 3 : 2);
    this.#prefetchMultiplePages('next', prefetchCount);
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#isPrefetching = false;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Disconnect prefetch observer - will be re-created after DOM update
    if (this.#prefetchObserver) {
      this.#prefetchObserver.disconnect();
      this.#prefetchObserver = undefined;
    }

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();
        this.#updateLoadMoreButton();
        this.#setupEarlyPrefetch();
        this.#isPrefetching = false;

        // Fetch the next pages - reduce on mobile
        const isMobile = window.innerWidth < 750;
        const viewMoreNext = this.refs.viewMoreNext;
        const isButtonMode = !!viewMoreNext?.querySelector('button[ref="loadMoreButton"]');
        const prefetchCount = isMobile ? (isButtonMode ? 2 : 1) : (isButtonMode ? 3 : 2);
        this.#prefetchMultiplePages('next', prefetchCount);
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
